/**
 * Cognito Authentication Client
 * 
 * Handles all authentication operations with AWS Cognito using
 * amazon-cognito-identity-js library.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';

// Cognito configuration from environment variables
const COGNITO_CONFIG = {
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
};

// Validate configuration
if (!COGNITO_CONFIG.UserPoolId || !COGNITO_CONFIG.ClientId) {
  console.warn('Cognito configuration missing. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID');
}

// Initialize the User Pool
const userPool = new CognitoUserPool(COGNITO_CONFIG);

// Types
export interface SignUpParams {
  email: string;
  password: string;
  name: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface CognitoUserInfo {
  sub: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * Sign up a new user
 */
export function signUp({ email, password, name }: SignUpParams): Promise<ISignUpResult> {
  const attributeList = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
    new CognitoUserAttribute({ Name: 'name', Value: name }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      if (!result) {
        reject(new Error('Sign up failed - no result returned'));
        return;
      }
      resolve(result);
    });
  });
}

/**
 * Confirm sign up with verification code
 */
export function confirmSignUp(email: string, code: string): Promise<string> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}

/**
 * Resend confirmation code
 */
export function resendConfirmationCode(email: string): Promise<string> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result?.CodeDeliveryDetails?.Destination || 'email');
    });
  });
}

/**
 * Sign in a user
 */
export function signIn({ email, password }: SignInParams): Promise<CognitoUserSession> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
      // Handle new password required (first login after admin creates user)
      newPasswordRequired: () => {
        reject(new Error('NEW_PASSWORD_REQUIRED'));
      },
    });
  });
}

/**
 * Sign out the current user
 */
export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

/**
 * Global sign out (invalidates all sessions)
 */
export function globalSignOut(): Promise<void> {
  const cognitoUser = userPool.getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    cognitoUser.globalSignOut({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Initiate forgot password flow
 */
export function forgotPassword(email: string): Promise<string> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        resolve(data.CodeDeliveryDetails?.Destination || 'email');
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Complete forgot password with new password
 */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<string> {
  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve('Password reset successful');
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Change password for authenticated user
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<string> {
  const cognitoUser = userPool.getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.reject(new Error('No user logged in'));
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null) => {
      if (err) {
        reject(err);
        return;
      }

      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result || 'Password changed successfully');
      });
    });
  });
}

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

/**
 * Get current session (will refresh if expired)
 */
export function getSession(): Promise<CognitoUserSession | null> {
  const cognitoUser = userPool.getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
}

/**
 * Get auth tokens from current session
 */
export async function getAuthTokens(): Promise<AuthTokens | null> {
  const session = await getSession();
  
  if (!session || !session.isValid()) {
    return null;
  }

  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
}

/**
 * Get user info from current session
 */
export async function getUserInfo(): Promise<CognitoUserInfo | null> {
  const session = await getSession();
  
  if (!session || !session.isValid()) {
    return null;
  }

  const idToken = session.getIdToken();
  const payload = idToken.payload;

  return {
    sub: payload.sub,
    email: payload.email,
    name: payload.name || '',
    emailVerified: payload.email_verified || false,
  };
}

/**
 * Get user attributes
 */
export function getUserAttributes(): Promise<Record<string, string>> {
  const cognitoUser = userPool.getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.reject(new Error('No user logged in'));
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null) => {
      if (err) {
        reject(err);
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const attrs: Record<string, string> = {};
        attributes?.forEach((attr) => {
          attrs[attr.Name] = attr.Value;
        });
        resolve(attrs);
      });
    });
  });
}

/**
 * Check if there is an authenticated user with valid session
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await getSession();
    return session !== null && session.isValid();
  } catch {
    return false;
  }
}

/**
 * Refresh the session
 */
export function refreshSession(): Promise<CognitoUserSession> {
  const cognitoUser = userPool.getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.reject(new Error('No user logged in'));
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session) {
        reject(new Error('No session'));
        return;
      }

      const refreshToken = session.getRefreshToken();
      
      cognitoUser.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(newSession);
      });
    });
  });
}

/**
 * Error code helpers
 */
export const CognitoErrorCodes = {
  UserNotConfirmedException: 'UserNotConfirmedException',
  NotAuthorizedException: 'NotAuthorizedException',
  UserNotFoundException: 'UserNotFoundException',
  UsernameExistsException: 'UsernameExistsException',
  CodeMismatchException: 'CodeMismatchException',
  ExpiredCodeException: 'ExpiredCodeException',
  InvalidPasswordException: 'InvalidPasswordException',
  InvalidParameterException: 'InvalidParameterException',
  LimitExceededException: 'LimitExceededException',
  TooManyRequestsException: 'TooManyRequestsException',
} as const;

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const code = (error as { code?: string }).code;
    
    switch (code) {
      case CognitoErrorCodes.UserNotConfirmedException:
        return 'Please verify your email address before signing in.';
      case CognitoErrorCodes.NotAuthorizedException:
        return 'Incorrect email or password.';
      case CognitoErrorCodes.UserNotFoundException:
        return 'No account found with this email address.';
      case CognitoErrorCodes.UsernameExistsException:
        return 'An account with this email already exists.';
      case CognitoErrorCodes.CodeMismatchException:
        return 'Invalid verification code. Please try again.';
      case CognitoErrorCodes.ExpiredCodeException:
        return 'Verification code has expired. Please request a new one.';
      case CognitoErrorCodes.InvalidPasswordException:
        return 'Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and numbers.';
      case CognitoErrorCodes.LimitExceededException:
      case CognitoErrorCodes.TooManyRequestsException:
        return 'Too many attempts. Please wait a moment and try again.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }
  return 'An unexpected error occurred.';
}
